import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Typography, Card, Input, Table, Tag, Space, Avatar, Button, Modal, Popconfirm, message } from "antd";
import { SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import CreateSupplierForm from "./createSupplierForm";
import SupplierService from "../../utils/api/supplierdirectory";
import LayoutPage from "../../layouts/Main";
import '../../scss/supplierStyle.scss';



const { Title, Text } = Typography;

function SupplierList() {


  const [editingSupplierId, setEditingSupplierId] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [savingSupplier, setSavingSupplier] = useState(false);

  const navigate = useNavigate();

  const categoryMap = {
    1: { name: "MRO", color: "blue" },
    2: { name: "Empaque", color: "green" },
    3: { name: "MRP", color: "purple" },
    4: { name: "Servicios", color: "orange" },
    5: { name: "Maquinado", color: "geekblue" },
    6: { name: "Tecnología", color: "cyan" }
  };

  function countCategories(suppliers) {
    const counts = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0
    };
    suppliers.forEach((supplier) => {
      if (counts[supplier.category] !== undefined) {
        counts[supplier.category]++;
      }
    });

    const counts_name = {
      MRO: counts[1],
      EMPAQUE: counts[2],
      MRP: counts[3],
      SERVICIOS: counts[4],
      MAQUINADO: counts[5],
      TECNOLOGIA: counts[6]
    };

    return counts_name;
  }
  const counts = countCategories(suppliers);

  const stats = [
    { label: "MRO", value: counts.MRO, id: 1 },
    { label: "Servicios", value: counts.SERVICIOS, id: 4 },
    { label: "Empaque", value: counts.EMPAQUE, id: 2 },
    { label: "Tecnología", value: counts.TECNOLOGIA, id: 6 },
    { label: "Maquinados", value: counts.MAQUINADO, id: 5 },
    { label: "MRP", value: counts.MRP, id: 3 }
  ];

  const openEditSupplier = (id) => {
    setEditingSupplierId(id);
    setOpenCreateModal(true);
  };

  const handleCloseModal = () => {
    if (savingSupplier) {
      return;
    }

    setOpenCreateModal(false);
    setEditingSupplierId(null);
  };

  const handleDelete = async (id) => {
    try {
      await SupplierService.DeleteSupplier(id);
      message.success("Proveedor eliminado");
      fetchSuppliers();
    } catch (error) {
      message.error("Error al eliminar proveedor");
    }
  };

  const fetchSuppliers = async () => {
    try {
      setLoading(true);

      const response = await SupplierService.ListSupplier();


      const suppliersArray = response.data;
      console.log("Suppliers API Response:", suppliersArray); // Debugging log
      const normalized = suppliersArray.map((item) => ({
        id: item.id,
        bussiness: item.bussiness || "-",
        category: item.category || "-",
        subcategory: item.subcategory || "-",
        description: item.description || "-",
        logo: item.logo || null,
        typeSupplier: item.typeSupplier,
        mainContact: item.mainContact || {
          phone: "-",
        },
        createdAt: item.createdAt ? new Date(item.createdAt) : null,
      })).sort((a, b) => {
       
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
   
        if (a.createdAt) return -1;
      
        if (b.createdAt) return 1;
       
        return 0;
      });

      setSuppliers(normalized);
      const counts = countCategories(normalized);


    } catch (error) {

      message.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {


    fetchSuppliers();

  }, []);

  const filteredData = useMemo(() => {
    const search = searchText.toLowerCase();

    return suppliers.filter((item) => {
      const matchSearch =
        item.bussiness?.toLowerCase().includes(search) ||
        item.subcategory?.toLowerCase().includes(search);

      const matchCategory =
        !selectedCategory || item.category === selectedCategory;

      return matchSearch && matchCategory;
    });
  }, [suppliers, searchText, selectedCategory]);


  const columns = [
    {
      title: "Empresa",
      dataIndex: "bussiness",
      flex: 1.2,
      minWidth: 200,
      render: (_, record) => (
        <div className="supplier-company">
          <Avatar size={40} style={{ marginRight: 15 }} src={record.logo} />
          <span>{record.bussiness}</span>
        </div>
      )
    },

    {
      title: "Categoria",
      dataIndex: "category",
 width: 140,
      filters: [
        { text: "MRO", value: 1, color: "blue" },
        { text: "Empaque", value: 2, color: "green" },
        { text: "MRP", value: 3, color: "purple" },
        { text: "Servicios", value: 4, color: "orange" },
        { text: "Maquinado", value: 5, color: "geekblue" },
        { text: "Tecnología", value: 6, color: "cyan" }
      ],
      onFilter: (value, record) => record.category === value,
      render: (category) => {
        const cat = categoryMap[category];
        return (
          <Tag color={cat?.color} className="category-tag">
            {cat?.name}
          </Tag>
        );
      }
    },

    {
      title: "Subcategoria",
      dataIndex: "subcategory",
    
      width: 200
    },

    {
      title: "Descripción",
      dataIndex: "description",
      width: 350,
      render: (text) => (
        <div className="description-cell" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {text}
        </div>
      )
    },

    {
      title: "Estado",
      dataIndex: "typeSupplier",
      width: 130,
      filters: [
        { text: "Activo", value: "ACTIVO" },
        { text: "Inactivo", value: "INACTIVO" },
        { text: "Referenciado", value: "REFERENCIADO" }
      ],
      onFilter: (value, record) => record.typeSupplier === value,
      render: (typeSupplier) => {
        const statusConfig = {
          ACTIVO: { color: "green", className: "status-active" },
          INACTIVO: { color: "red", className: "status-inactive" },
          REFERENCIADO: { color: "blue", className: "status-referenced" }
        };

        const config = statusConfig[typeSupplier] || {
          color: "default",
          className: "status-default"
        };

        return (
          <Tag color={config.color} className={config.className}>
            {typeSupplier}
          </Tag>
        );
      }
    },

    {
      title: "Contactos",
    width: 220,
      render: (_, record) => (
        <div className="contact-cell">
          <div>{record.mainContact?.name}</div>
          <div>{record.mainContact?.email}</div>
          <div>{record.mainContact?.phone}</div>
        </div>
      )
    },

    {
      title: "Acciones",
   width: 150,

      fixed: 'right',
      render: (_, record) => (
        <Space>

          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/@p/${record.id}`)}
          />

          {record.typeSupplier === "REFERENCIADO" && (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => openEditSupplier(record.id)}
              />

              <Popconfirm class="delete-popconfirm"
                title="¿Eliminar proveedor?"
                onConfirm={() => handleDelete(record.id)}
              >
                <Button icon={<DeleteOutlined />} />
              </Popconfirm>

            </>
          )}

        </Space>
      )
    }
  ];


  return (

    <LayoutPage>
      <div className="full-width-page">
      <Row gutter={[24, 24]} style={{ marginLeft: 18, marginRight: 18 }}>
        <Col >
          <div> <Title level={1} style={{ color: "#1E3A8A", fontWeight: 600 }}>
            DIRECTORIO DE PROVEEDORES</Title>
          </div>
        </Col>
      </Row>
      <Row gutter={[24, 24]} style={{ marginTop: 30, marginBottom: 30, marginLeft: 18, marginRight: 18 }}>
        {stats.map((item) => (<Col xs={12} sm={12} md={8} lg={6} xl={4} key={item.label} >
          <Card onClick={() => setSelectedCategory(selectedCategory === item.id ? null : item.id)}
            style={{
              borderRadius: 12,
              boxShadow: selectedCategory === item.id ? "0 4px 12px rgba(0, 10, 120, 0.83)" : "0 4px 12px rgba(13, 0, 133, 0.05)",
              textAlign: "center",
              backgroundColor: selectedCategory === item.id ? "#ffffff" : "#FFFFFF",
              cursor: "pointer",
              padding: 20,
              transition: "all 0.3s ease"
            }} >
            <Text type="secondary"  style={{
    color: "#1E3A8A",
    fontSize: 16,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  }}>
              {item.label}
            </Text>
            <Title level={3} style={{ margin: 5, color: "#1E3A8A", fontSize: 18, fontWeight: 600 }}>
              {item.value}
            </Title>
          </Card>
        </Col>
        ))}


      </Row>


      <div style={{ padding: 32, width: '100%', boxSizing: 'border-box' }}>
        <div className="filters-section" style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <div className="search-filter" style={{ flex: 1, minWidth: 200 }}>

            <Input
              placeholder="Buscar proveedor..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid #d9d9d9'
              }}
            />

          </div>
          <div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={loading}
              size="large"
              style={{
                backgroundColor: '#0f5197',
                borderColor: '#0f5197',
                borderRadius: '8px',
                height: '40px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                boxShadow: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#002140';
                e.currentTarget.style.borderColor = '#002140';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#0f5197';
                e.currentTarget.style.borderColor = '#0f5197';
              }}
              onClick={() => setOpenCreateModal(true)}
            >
              Agregar proveedor
            </Button>
          </div>
        </div>
        <div style={{ borderRadius: 12, overflowX: 'auto', overflowY: 'hidden', background: '#fff', boxShadow: "0 4px 12px rgba(13, 0, 133, 0.05)", width: '100%', boxSizing: 'border-box' }}>
          <Table
            className="supplier-table"
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            loading={loading}
            bordered={true}
            size="middle"
            pagination={{ pageSize: 10, align: "center", showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
           scroll={{ x: 'max-content' }}
style={{ width: '100%' }}
tableLayout="fixed"
          />
        </div>

      </div>
      <Modal
        title={editingSupplierId ? "Editar Proveedor" : "Nuevo Proveedor"}
        open={openCreateModal}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        closable={!savingSupplier}
        maskClosable={!savingSupplier}
        keyboard={!savingSupplier}
        destroyOnClose
        afterClose={() => {
          setEditingSupplierId(null);
          setSavingSupplier(false);
        }}

      >
        <CreateSupplierForm
          supplierId={editingSupplierId}
          onLoadingChange={setSavingSupplier}
          onSuccess={async () => {
            setOpenCreateModal(false);
            setEditingSupplierId(null);
            setSavingSupplier(false);
            await new Promise(resolve => setTimeout(resolve, 500));
            await fetchSuppliers();
          }}
        />
      </Modal>

    </div>
    </LayoutPage >
  );
}

export default SupplierList;
